/**
 * Review marquee — manual drag/swipe + review lightbox.
 * Loaded only when `enable_swipe` is on; without it the section stays zero-JS.
 *
 * The marquee itself remains a pure CSS @keyframes animation. Dragging never sets a
 * transform — it moves the animation's own playhead via `animation.currentTime`, so
 * the manual and automatic positions are the same value and releasing cannot jump.
 */
(function () {
  const DRAG_THRESHOLD = 8;
  const SWIPE_THRESHOLD = 40;

  /** Wraps t into [0, duration) so the playhead never drifts out of range. */
  function wrapTime(t, duration) {
    return ((t % duration) + duration) % duration;
  }

  /**
   * Shared pointer gesture: reports horizontal travel and suppresses the click that
   * follows a drag, so a swipe can never open the lightbox or hit a backdrop close.
   *
   * onMove receives total dx since pointerdown (not per-event deltas), so callers
   * derive absolute state and never accumulate rounding error.
   */
  function onDrag(el, { onStart, onMove, onEnd }) {
    let id = null;
    let startX = 0;
    let dx = 0;
    let dragging = false;
    let suppressClick = false;

    el.addEventListener('pointerdown', (event) => {
      if (id !== null || event.button > 0) return;
      id = event.pointerId;
      startX = event.clientX;
      dx = 0;
      dragging = false;
      // Reset here rather than after the click: a gesture cancelled by a vertical
      // scroll never produces a click, and a stale flag would eat the next tap.
      suppressClick = false;
      if (onStart) onStart();
    });

    el.addEventListener('pointermove', (event) => {
      if (event.pointerId !== id) return;
      dx = event.clientX - startX;

      if (!dragging && Math.abs(dx) > DRAG_THRESHOLD) {
        dragging = true;
        // Captured only once the gesture is known to be horizontal, so `touch-action:
        // pan-y` keeps vertical page scrolling native up to that point.
        if (el.setPointerCapture) el.setPointerCapture(id);
        el.classList.add('is-dragging');
      }
      if (dragging && onMove) onMove(dx);
    });

    const finish = (event) => {
      if (event.pointerId !== id) return;
      id = null;
      el.classList.remove('is-dragging');
      suppressClick = dragging;
      if (onEnd) onEnd(dragging, dx);
    };

    el.addEventListener('pointerup', finish);
    el.addEventListener('pointercancel', finish);

    el.addEventListener(
      'click',
      (event) => {
        if (!suppressClick) return;
        suppressClick = false;
        event.preventDefault();
        event.stopPropagation();
      },
      true
    );
  }

  class ReviewMarquee extends HTMLElement {
    connectedCallback() {
      this.viewport = this.querySelector('.review-marquee__viewport');
      this.track = this.querySelector('.review-marquee__track');
      if (!this.viewport || !this.track) return;

      // Reverse animation-direction means a rising currentTime moves content right,
      // so the finger-follow sign flips. Taken from the merchant's direction setting,
      // never from computed `direction: rtl`.
      this.sign = this.viewport.dataset.direction === 'right' ? 1 : -1;
      this.copies = parseFloat(this.viewport.dataset.copies) || 1;

      this.bindDrag();
      this.bindLightbox();
    }

    bindDrag() {
      // Under prefers-reduced-motion the track has `animation: none`, so there is no
      // timeline to scrub. The lightbox is still wired below.
      if (!this.track.getAnimations || this.track.getAnimations().length === 0) return;

      this.viewport.classList.add('review-marquee__viewport--draggable');

      let anim = null;
      let baseTime = 0;
      let duration = 0;
      let msPerPx = 0;

      onDrag(this.viewport, {
        onStart: () => {
          anim = this.track.getAnimations()[0];
          if (!anim) return;

          duration = anim.effect.getTiming().duration;
          // currentTime is a plain millisecond number for document-timeline
          // animations; bail rather than guess if a browser hands back anything else.
          baseTime = typeof anim.currentTime === 'number' ? anim.currentTime : NaN;
          if (typeof duration !== 'number' || !isFinite(duration) || !isFinite(baseTime)) {
            anim = null;
            return;
          }

          // The only layout read of the whole gesture, cached here and never touched
          // in pointermove.
          const groupWidth = this.track.offsetWidth / this.copies;
          msPerPx = groupWidth > 0 ? duration / groupWidth : 0;
        },
        onMove: (dx) => {
          if (!anim || !msPerPx) return;
          anim.currentTime = wrapTime(baseTime + this.sign * dx * msPerPx, duration);
        },
        onEnd: () => {
          anim = null;
        },
      });
    }

    bindLightbox() {
      const selector = this.dataset.lightbox;
      if (!selector || selector === '#') return;
      const modal = document.querySelector(selector);
      if (!modal) return;

      this.addEventListener('click', (event) => {
        const opener = event.target.closest('.review-marquee__open');
        if (!opener) return;
        event.preventDefault();
        if (typeof modal.openAt === 'function') {
          modal.openAt(parseInt(opener.dataset.index, 10) || 0, opener);
        }
      });
    }
  }

  if (!customElements.get('review-marquee')) {
    customElements.define('review-marquee', ReviewMarquee);
  }

  // ModalDialog (global.js) ships Escape, backdrop click, the close button and focus
  // trapping. Only navigation is added here.
  if (typeof ModalDialog === 'undefined' || customElements.get('review-lightbox')) return;

  class ReviewLightbox extends ModalDialog {
    constructor() {
      super();
      this.image = this.querySelector('.review-lightbox__image');
      this.counter = this.querySelector('.review-lightbox__counter');
      this.index = 0;

      // Canonical URL list: only the non-duplicated group carries data-full, so the
      // modal holds one <img> no matter how large the gallery is.
      this.sources = Array.from(document.querySelectorAll(`[data-lightbox-source="${this.id}"]`)).map(
        (el) => el.dataset.full
      );

      this.querySelectorAll('.review-lightbox__nav').forEach((button) => {
        button.addEventListener('click', (event) => {
          event.stopPropagation();
          this.step(button.dataset.step === 'next' ? 1 : -1);
        });
      });

      this.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowRight') this.step(1);
        else if (event.key === 'ArrowLeft') this.step(-1);
        else return;
        event.preventDefault();
      });

      // Swipe between images. The shared handler also stops the release from
      // reaching ModalDialog's backdrop-close listener.
      onDrag(this, {
        onEnd: (dragging, dx) => {
          if (dragging && Math.abs(dx) > SWIPE_THRESHOLD) this.step(dx < 0 ? 1 : -1);
        },
      });
    }

    openAt(index, opener) {
      this.render(index);
      this.show(opener);
    }

    step(delta) {
      if (!this.sources.length) return;
      this.render((this.index + delta + this.sources.length) % this.sources.length);
    }

    render(index) {
      if (!this.sources.length) return;
      this.index = index;
      if (this.image) this.image.src = this.sources[index];
      if (this.counter) this.counter.textContent = `${index + 1} / ${this.sources.length}`;
    }
  }

  customElements.define('review-lightbox', ReviewLightbox);
})();
